import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LoadingButton } from "@mui/lab";
import { Button, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import { makeStyles } from "@mui/styles";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as ShareIcon } from "../../../assets/icons/share.svg";
import { AuthContext } from "../../../contexts/AuthContext";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { WorkspacesContext } from '../../../contexts/WorkspacesContext';
import useBackwardsCompatible from "../../../hooks/useBackwardsCompatible";
import useBulkDashboards from "../../../hooks/useBulkDashboards";
import useBulkWorkspaces from "../../../hooks/useBulkWorkspaces";
import { sendShareWorkspaceEmailFunc, updateWorkspaceFunc } from "../../../lib/helper";
import { Dashboard, Workspace } from "../../../types/files";
import ArrayUtils from "../../../utils/ArrayUtils";
import { collectionKey as partialKey } from "../../pages/InvestorsPage";
import configuredAsyncComponentLoader from "../../templates/loader";

const FoundInvestors = configuredAsyncComponentLoader(() => import('../../atoms/multi-action/FoundInvestors'));
const CopyDealSnippets = configuredAsyncComponentLoader(() => import('../../atoms/multi-action/CopyDealSnippets'));

const useSuccessStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            maxWidth: 'unset',
            maxHeight: 'unset',
            width: 'fit-content',
            height: 'fit-content',
            padding: '16px 24px',
            borderRadius: 24,
        },
    },
    dialogTitle: {
        display: 'flex',
        width: 'auto',
        height: 'auto',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.3rem',
    },
    dialogContent: {
        display: 'flex',
        width: 'auto',
        height: 'auto',
        flexDirection: 'column',
        padding: '8px 0',
        overflowY: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        width: 'auto',
        height: 'auto',
        flexDirection: 'column',
        alignItems: 'center',
    },
    mailingList: {
        maxHeight: 240,
        height: '100%',
        width: '100%',
        overflowY: 'auto',
    },
    sharedIcon: {
        width: 40,
        height: 40,
        color: theme.palette.primary.main,
    },
    successText : {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['800'],
    },
    linkText: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['600'],
    },
    commonButton: {
        minWidth: 80,
        width: 'fit-content',
        borderRadius: 20,
        background: theme.palette.primary.main,
        color: 'white',
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
}));

const useMainStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            maxWidth: '1200px',
            width: '80vw',
            height: '80vh',
            padding: 16,
            borderRadius: 24,
            overflow: 'hidden',
        },
    },
    dialogTitle: {
        display: 'flex',
        width: '100%',
        height: 'auto',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        color: 'black',
        fontSize: '1.5rem',
        padding: 'unset',
        gap: 8,
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        height: '100%',
        overflowY: 'auto',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        color: '#666666',
    },
    cancelButton: {
        width: 100,
        borderRadius: 40,
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['600'],
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    saveAllButton: {
        minWidth: 100,
        width: 'auto',
        paddingLeft: 16,
        paddingRight: 16,
        borderRadius: 20,
        borderColor: theme.colors.neutral['100'],
        background: '#E0F1F1',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        "& > span": {
            margin: 'unset',
        }
    },
    loading: {
        width: '100%',
        height: '100%',
        color: theme.palette.primary.main,
    },
    commonButton: {
        height: 40,
        borderRadius: 32,
        textTransform: 'none',
        fontSize: '0.95rem',
        fontWeight: 'bold',
    },
    icon: {
        width: 32,
        height: 32,
        stroke: '#7bd4d4',
    }
}));

export const collectionKey = `${partialKey}:deals`;
const CountDownSec = 3;

const FindInvestorsShareDealSuccessModal: React.FC<{
    isOpen: boolean,
    mailables: {workspace: Workspace, dashboards: Dashboard[]}[],
    onClose: () => void,
}> = ({ isOpen, mailables, onClose }) => {
    const classes = useSuccessStyles();
    const [countDown, setCountDown] = useState<number>(CountDownSec);
    const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setCountDown(prev => {
                if (prev > 1) {
                    return prev - 1;
                } else {
                    clearInterval(timerRef.current);
                    onClose();
                    return prev;
                }
            });
        }, 1000);

        return () => {
            clearInterval(timerRef.current);
        }
    }, [onClose]);

    return (<>
        <Dialog className={classes.dialog} open={isOpen} onClose={onClose}>
            <DialogContent className={classes.dialogContent}>
                <Stack spacing={2} alignItems="center" justifyContent="center" width="100%" py={3}>
                    <CheckCircleIcon className={classes.sharedIcon} />
                    <Typography className={classes.successText}>
                        {'Deals successfully sent'}
                    </Typography>
                </Stack>
            </DialogContent>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogActions className={classes.dialogActions}>
                <Stack direction="row" alignItems="center" justifyContent="center" width="100%">
                    <Button variant="contained"
                        className={classes.commonButton}
                        onClick={onClose}> {`Close (${countDown})`} </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    </>);
}

const FindInvestorsShareDealModal: React.FC<{
    isOpen: boolean,
    dashboard?: Dashboard,
    onClose: () => void,
}> = ({ isOpen, dashboard, onClose }) => {
    const classes = useMainStyles();
    const { user } = useContext(AuthContext);
    const { setWorkspaces } = useContext(WorkspacesContext);
    const { dashboards, setDashboards } = useContext(DashboardsContext);
    const { dashboard: globalDashboard, setDashboard } = useContext(DashboardContext);
    const { getBulkWorkspaces, assignToDashboards } = useBulkWorkspaces();
    const { getBulkDashboards, addToBulk: addDashboard } = useBulkDashboards();
    const { dashboardWorkspaces } = useBackwardsCompatible();

    const [loading, setLoading] = useState<boolean>(false);
    const [shared, setShared] = useState<boolean>(false);
    const [mailables, setMailables] = useState<{workspace: Workspace, dashboards: Dashboard[]}[]>([]);
    const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    const localDashboard = useMemo(() => dashboard ?? globalDashboard, [dashboard, globalDashboard]);
    const collectionKey = useMemo(() => `dashboard[${localDashboard?.id}]`, [localDashboard]);
    const bulkWorkspaces = useMemo(() => getBulkWorkspaces(collectionKey), [collectionKey, getBulkWorkspaces]);
    const bulkDashboards = useMemo(() =>  getBulkDashboards(collectionKey).map(({dashboard}) => dashboard), [collectionKey, getBulkDashboards]);

    const handleClose = useCallback(() => {
        setLoading(false);
        timerRef.current = setTimeout(() => {
            onClose();
        }, 200);

        return () => {
            clearTimeout(timerRef.current);
        }
    }, [onClose]);

    const handleShareEmails = useCallback(async () => {
        return new Promise<Dashboard[] | any>(async (resolve) => {
            const updatePromises: Promise<Workspace>[] = [];
            const assignPromises: Promise<Dashboard[]>[] = [];
            const sharePromises: Promise<string | any>[] = [];

            setLoading(true);
            mailables.forEach(({workspace, dashboards}) => {
                if (!workspace.isPublic)
                    updatePromises.push(updateWorkspaceFunc({...workspace, isPublic: true, }));

                assignPromises.push(new Promise<Dashboard[]>(async (resolve) => {
                    const unassignedIds = dashboards.filter(dashboard =>
                        !dashboardWorkspaces(dashboard).includes(workspace.id)
                    ).map(dashboard => dashboard.id);
                    const dashboardsData: Dashboard[] = await assignToDashboards(collectionKey, unassignedIds) ?? [];

                    resolve(dashboardsData);
                }));

                sharePromises.push(sendShareWorkspaceEmailFunc({
                    workspaceId: workspace.id,
                    toAddress: workspace.email,
                    replyToAddress: user.attributes.email,
                    dashboardIds: dashboards.map(dashboard => dashboard.id),
                }));
            });

            Promise.all([Promise.all(updatePromises), Promise.all(assignPromises)])
                .then(async ([updatedWorkspaces, multiUpdatedDashboards]) => {
                const updatedDashboards = multiUpdatedDashboards.flat();

                setWorkspaces(prev => prev.map(ws => updatedWorkspaces?.find(updated => updated.id === ws.id) ?? ws));
                setDashboards(prev => ArrayUtils.sortByDescending(prev.map(current => {
                    const updates = updatedDashboards.filter(updated => updated.id === current.id);

                    if (!!updates.length) {
                        const dashboardUpdate = ArrayUtils.sortByDescending(updates, 'updatedAt')[0];

                        if (localDashboard && (localDashboard?.id === current.id))
                            setDashboard(dashboardUpdate);

                        return dashboardUpdate;
                    }

                    return current;
                }), 'createdAt'));

                await Promise.all(sharePromises);
                resolve(true);
            }).finally(() => setShared(true));
        });
    // eslint-disable-next-line
    }, [mailables, localDashboard, user]);

    useEffect(() => {
        setMailables(bulkWorkspaces.map(workspace => ({ workspace,
            dashboards: !!bulkDashboards.length ? bulkDashboards
                : dashboards.filter(dashboard => dashboardWorkspaces(dashboard).includes(workspace.id)),
        })));
    // eslint-disable-next-line
    }, [dashboards, bulkWorkspaces, bulkDashboards]);

    useEffect(() => {
        if (!!localDashboard) {
            if (!bulkDashboards.find(d => d.id === localDashboard.id))
                addDashboard(collectionKey, localDashboard);
        }
    // eslint-disable-next-line
    }, [bulkDashboards, collectionKey, localDashboard]);

    if (!localDashboard)
        return (<></>);

    return (<>
        {shared ? (<>
            <FindInvestorsShareDealSuccessModal
                isOpen={shared} mailables={mailables} onClose={handleClose} />
        </>) : (<>
            <Dialog className={classes.dialog} open={isOpen} onClose={handleClose}>
                <DialogTitle className={classes.dialogTitle}>
                    <ShareIcon className={classes.icon} />
                    {`Find investors and share ${localDashboard.title}`}
                </DialogTitle>
                <Divider sx={{ margin: '8px 0'}} />
                <DialogContent className={classes.dialogContent}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between"
                        width="100%" height="100%" overflow="hidden">
                        <Stack width="50%" height="100%" px={2}>
                            <FoundInvestors dashboards={[localDashboard]}
                                collectionKey={`dashboard[${localDashboard.id}]`} />
                        </Stack>
                        <Divider orientation="vertical" sx={{ margin: '8px 0'}} />
                        <Stack width="50%" height="100%" px={2}>
                            <CopyDealSnippets dashboards={[localDashboard]} />
                        </Stack>
                    </Stack>
                </DialogContent>
                <Divider sx={{ margin: '8px 0'}} />
                <DialogActions className={classes.dialogActions}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" width="100%">
                        <Button className={classes.cancelButton}
                            onClick={handleClose}> {'Cancel'} </Button>
                        <LoadingButton variant="contained"
                            className={classes.commonButton}
                            disabled={!bulkWorkspaces.length}
                            loading={loading}
                            onClick={handleShareEmails}
                            sx={{ width: 120 }}>{'Send'}</LoadingButton>
                    </Stack>
                </DialogActions>
            </Dialog>
        </>)}
    </>);
}

export default FindInvestorsShareDealModal;